import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { GlobeHemisphereWest, Plus, LockSimple, Globe, SpinnerGap, MapPinLine } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/PageHero";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AtlasList() {
  const [maps, setMaps] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [pub, setPub] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/atlas/maps").then((r) => setMaps(r.data.maps)).catch(() => setMaps([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Give your map a name"); return; }
    setBusy(true);
    try {
      await api.post("/atlas/maps", { name: name.trim(), description: desc.trim(), visibility: pub ? "public" : "private" });
      toast.success("Map created");
      setOpen(false); setName(""); setDesc(""); setPub(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create map"); }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHero
        seed="living-atlas" icon={GlobeHemisphereWest}
        eyebrow="Living Atlas"
        title="Your travel maps"
        subtitle="Every located photo & video paints your world. Build private or public maps, mark places you've visited, lived, or want to see next."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-map-btn" className="h-11 gap-1.5 rounded-full bg-white px-5 text-[#2C2724] hover:bg-white/90">
                <Plus size={16} weight="bold" /> New map
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-xl">
              <DialogHeader><DialogTitle>Create a new atlas map</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="overline mb-1 block text-[10px] text-muted-foreground">Map name</label>
                  <input data-testid="map-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. My World, Family Trips 2026" className="h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
                </div>
                <div>
                  <label className="overline mb-1 block text-[10px] text-muted-foreground">Description (optional)</label>
                  <textarea data-testid="map-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)}
                    rows={2} className="w-full rounded-md border border-border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
                </div>
                <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border p-3" data-testid="map-public-toggle">
                  <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
                  <span>
                    <span className="block text-sm font-bold">Make this map public</span>
                    <span className="block text-xs text-muted-foreground">Anyone with the share link can view it (read-only). You can change this anytime.</span>
                  </span>
                </label>
                <Button data-testid="map-create-submit" onClick={create} disabled={busy} className="h-10 w-full rounded-md bg-[#F27A54] hover:bg-[#d9633f]">
                  {busy ? <SpinnerGap size={16} className="animate-spin" /> : "Create map"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {maps === null ? (
        <div className="mt-16 grid place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" /></div>
      ) : maps.length === 0 ? (
        <div className="mt-16 grid place-items-center text-center">
          <GlobeHemisphereWest size={48} weight="thin" className="text-zinc-300" />
          <p className="mt-3 font-display font-bold">No maps yet</p>
          <p className="text-sm text-muted-foreground">Create your first map to start painting the world.</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {maps.map((m) => (
            <Link key={m.id} to={`/atlas/${m.id}`} data-testid={`map-card-${m.id}`}
              className="group overflow-hidden rounded-3xl border border-[#E5DFD3] bg-white p-5 shadow-[0_8px_30px_rgba(44,39,36,0.05)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-center justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-2xl brand-sunset text-white"><GlobeHemisphereWest size={22} weight="fill" /></span>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.visibility === "public" ? "bg-[#2D6A4F]/12 text-[#2D6A4F]" : "bg-[#F0EBE1] text-[#787167]"}`}>
                  {m.visibility === "public" ? <Globe size={11} weight="fill" /> : <LockSimple size={11} weight="fill" />}
                  {m.visibility}
                </span>
              </div>
              <h3 className="mt-3 font-display text-lg font-black text-[#2C2724]">{m.name}</h3>
              {m.description && <p className="mt-1 line-clamp-2 text-xs text-[#787167]">{m.description}</p>}
              <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-[#F27A54]">
                <MapPinLine size={14} weight="bold" /> Open map →
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
